import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  Form,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import {
  BsArrowRepeat,
  BsFilter,
  BsPencil,
  BsPersonDash,
  BsThreeDots,
  BsTrash,
} from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import styles from "./userPage.module.scss";
import userPageSlice from "./store/slice";
import { User } from "../users/store/slice";
import RoleBadge from "../role-badge/RoleBadge";
import defaultSelectStyle from "../../utils/select";
import Select, { MultiValue, StylesConfig } from "react-select";
import companySlice from "../companies/store/slice";
import UnassignModal from "../unassign-modal/UnassignModal";
import bookingFilterSlice from "../booking-filters/store/slice";
import { useNavigate } from "react-router-dom";

const selectStyles: StylesConfig<RoleOption> = {
  ...(defaultSelectStyle as StylesConfig<RoleOption>),
  container: (base) => ({
    ...base,
    minWidth: "200px",
    fontSize: "14px",
  }),
  input: (base) => ({
    ...base,
  }),
};

type UserForm = {
  id?: number;
  email: string;
  name: string;
  company_ids: number[];
  role_ids: number[];
};

type CompanyOption = {
  label: string;
  value: number;
};

type RoleOption = {
  label: string;
  value: number;
};

const UserPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [selectedCompanyOptions, setSelectedCompanyOptions] = useState<
    MultiValue<CompanyOption>
  >([]);
  const [selectedRoleOptions, setSelectedRoleOptions] = useState<
    MultiValue<RoleOption>
  >([]);
  const [selectedForUnassign, setSelectedForUnassign] = useState<User>();

  const data = useSelector((state) => state.userPage.data);
  const meta = useSelector((state) => state.userPage.meta);
  const isLoading = useSelector((state) => state.userPage.isLoading);
  const isCreating = useSelector((state) => state.userPage.isCreating);
  const isUpdating = useSelector((state) => state.userPage.isUpdating);
  const retryingUserId = useSelector((state) => state.userPage.retryingUserId);
  const editableIndex = useSelector((state) => state.userPage.editableIndex);
  const roles = useSelector((state) => state.roles.data);
  const rolesLoading = useSelector((state) => state.roles.isLoading);
  const companies = useSelector((state) => state.companies.data);
  const companiesLoading = useSelector((state) => state.companies.isLoading);
  const search = useSelector((state) => state.userPage.search);

  const loadMore = useCallback(
    () => dispatch(userPageSlice.actions.loadMore()),
    [dispatch]
  );
  const create = useCallback(
    (user: UserForm) => dispatch(userPageSlice.actions.create(user)),
    [dispatch]
  );
  const addUser = useCallback(
    (user: Omit<User, "id" | "active" | "is_verified" | "is_girteka_user">) =>
      dispatch(userPageSlice.actions.addUser(user)),
    [dispatch]
  );
  const removeUser = useCallback(
    () => dispatch(userPageSlice.actions.removeUser()),
    [dispatch]
  );
  const update = useCallback(
    (user: UserForm) => dispatch(userPageSlice.actions.update(user)),
    [dispatch]
  );
  const remove = useCallback(
    (user: User) => dispatch(userPageSlice.actions.remove(user)),
    [dispatch]
  );
  const restore = useCallback(
    (user: User) => dispatch(userPageSlice.actions.restore(user)),
    [dispatch]
  );
  const setEditableIndex = useCallback(
    (index?: number) => dispatch(userPageSlice.actions.setEditableIndex(index)),
    [dispatch]
  );
  const reset = useCallback(
    () => dispatch(userPageSlice.actions.reset()),
    [dispatch]
  );
  const fetchCompanies = useCallback(
    (name: string) => dispatch(companySlice.actions.fetchData(name)),
    [dispatch]
  );
  const resetCompanies = useCallback(
    () => dispatch(companySlice.actions.reset()),
    [dispatch]
  );
  const setSearch = useCallback(
    (name: string) => dispatch(userPageSlice.actions.setSearch(name)),
    [dispatch]
  );
  const retryInvite = useCallback(
    (id: number) => dispatch(userPageSlice.actions.retryInvite(id)),
    [dispatch]
  );
  const setCustomers = useCallback(
    (customers: User[]) =>
      dispatch(bookingFilterSlice.actions.setCustomers(customers)),
    [dispatch]
  );

  const companyOptions: CompanyOption[] = useMemo(
    () =>
      companies.map((company) => ({
        label: `${company.name} (${company.ax_code})`,
        value: company.id!,
      })),
    [companies]
  );

  const roleOptions: RoleOption[] = useMemo(
    () =>
      roles.map((role) => ({
        label: role.name,
        value: role.id,
      })),
    [roles]
  );

  useEffect(() => {
    setSearch("");

    return () => {
      reset();
    };
  }, [setSearch, reset]);

  useEffect(() => {
    fetchCompanies("");

    return () => {
      resetCompanies();
    };
  }, [fetchCompanies, resetCompanies]);

  const editMode = typeof editableIndex !== "undefined";

  return (
    <Container fluid>
      <Row>
        <Col />
        <Col lg={8}>
          <Card className={styles.card}>
            <Card.Body>
              <Card.Title className={styles.cardTitle}>
                <Container fluid className="g-0">
                  <Row className="gy-3">
                    <Col xs={12} sm={6}>
                      Users {meta && `(${meta.total})`}
                    </Col>
                    <Col
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Form.Control
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        type="text"
                        placeholder="Search"
                        disabled={editMode}
                        style={{ marginRight: "1rem", maxWidth: "300px" }}
                      />

                      <Button
                        variant="primary"
                        disabled={editMode}
                        onClick={() => {
                          addUser({
                            email,
                            name,
                          });

                          setEmail("");
                          setName("");
                          setSelectedCompanyOptions([]);
                          setSelectedRoleOptions([]);

                          setEditableIndex(0);
                        }}
                      >
                        Create
                      </Button>
                    </Col>
                  </Row>
                </Container>
              </Card.Title>

              <hr />

              <Table
                hover
                responsive
                striped
                borderless
                className={styles.table}
              >
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Companies</th>
                    <th>Roles</th>
                    <th>Invitation</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!!(isLoading && data.length === 0) && (
                    <tr className={styles.loading}>
                      <td colSpan={8}>
                        <Spinner
                          className={`text-primary ${styles.initialSpinner}`}
                          animation="border"
                        />
                      </td>
                    </tr>
                  )}

                  {data.map((user, index) => {
                    const isDisabled = !!(editMode && editableIndex !== index);
                    const isActive = editableIndex === index;

                    return (
                      <tr key={index}>
                        <td>
                          {isActive ? (
                            <Form.Control
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              size="sm"
                              disabled={user.is_girteka_user}
                            />
                          ) : (
                            <span>
                              {user.email}{" "}
                              {user.is_deleted && (
                                <Badge bg="danger">Deleted</Badge>
                              )}
                            </span>
                          )}
                        </td>
                        <td>
                          {isActive ? (
                            <Form.Control
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              size="sm"
                              disabled={user.is_girteka_user}
                            />
                          ) : (
                            user.name
                          )}
                        </td>
                        <td>
                          {isActive ? (
                            <Select
                              value={selectedCompanyOptions}
                              onChange={setSelectedCompanyOptions}
                              onInputChange={(inputValue, actionMeta) => {
                                if (actionMeta.action === "input-change") {
                                  fetchCompanies(inputValue);
                                }
                              }}
                              options={companyOptions}
                              isLoading={companiesLoading}
                              placeholder="Search"
                              styles={selectStyles}
                              isMulti
                            />
                          ) : (
                            user.companies && (
                              <>
                                {user.companies.length > 0 && (
                                  // <OverlayTrigger
                                  //   placement="top"
                                  //   overlay={(props) => (
                                  //     <Tooltip
                                  //       id={`user-${user.id}-companies-tooltip`}
                                  //       {...props}
                                  //     >
                                  //       <ul className={styles.companyTooltip}>
                                  //         {user.companies &&
                                  //           user.companies.map(
                                  //             (company, index) => (
                                  //               <li key={index}>
                                  //                 {company.name} (
                                  //                 {company.ax_code})
                                  //               </li>
                                  //             )
                                  //           )}
                                  //       </ul>
                                  //     </Tooltip>
                                  //   )}
                                  // >
                                  <span>
                                    {user.companies[0].name}{" "}
                                    <span className="text-secondary">
                                      ({user.companies[0].ax_code})
                                    </span>
                                    {user.companies.length > 1 &&
                                      ` and ${user.companies.length - 1} more.`}
                                  </span>
                                  // </OverlayTrigger>
                                )}
                                {user.companies.length === 0 && "-"}
                              </>
                            )
                          )}
                        </td>
                        <td className={styles.roles}>
                          {isActive ? (
                            <Select
                              value={selectedRoleOptions}
                              onChange={setSelectedRoleOptions}
                              options={roleOptions}
                              isLoading={rolesLoading}
                              placeholder="Search"
                              styles={selectStyles}
                              isMulti
                            />
                          ) : (
                            user.roles && (
                              <>
                                {user.roles.map((role, index) => (
                                  <RoleBadge key={index} name={role.name} />
                                ))}
                                {user.roles.length === 0 && "-"}
                              </>
                            )
                          )}
                        </td>
                        <td>
                          {user.is_girteka_user ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={(props) => (
                                <Tooltip
                                  id={`girteka-user-${user.id}-tooltip`}
                                  {...props}
                                >
                                  User signed in with Girteka account.
                                  Invitation not needed.
                                </Tooltip>
                              )}
                            >
                              <Badge bg="secondary">Girteka</Badge>
                            </OverlayTrigger>
                          ) : user.is_verified ? (
                            <Badge bg="success">Accepted</Badge>
                          ) : (
                            <>
                              <Badge bg="secondary">Pending</Badge>
                              {retryingUserId === user.id && (
                                <Spinner
                                  size="sm"
                                  animation="border"
                                  className={`${styles.spinner} text-secondary`}
                                />
                              )}
                            </>
                          )}
                        </td>
                        <td>
                          <div className={styles.actions}>
                            {isActive ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="success"
                                  className={styles.loadButton}
                                  style={{ marginRight: "0.5rem" }}
                                  onClick={() => {
                                    const body: UserForm = {
                                      email,
                                      name,
                                      company_ids: selectedCompanyOptions.map(
                                        (option) => option.value
                                      ),
                                      role_ids: selectedRoleOptions.map(
                                        (option) => option.value
                                      ),
                                    };

                                    if (
                                      editableIndex === 0 &&
                                      !data[editableIndex].id
                                    ) {
                                      create(body);
                                    } else {
                                      update({
                                        id: data[editableIndex].id,
                                        ...body,
                                      });
                                    }
                                  }}
                                >
                                  Save
                                  {!!(isCreating || isUpdating) && (
                                    <Spinner
                                      size="sm"
                                      animation="border"
                                      className={styles.spinner}
                                    />
                                  )}
                                </Button>

                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => {
                                    if (!data[editableIndex].id) {
                                      removeUser();
                                    }

                                    setEditableIndex(undefined);
                                  }}
                                  disabled={!!(isCreating || isUpdating)}
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <Dropdown drop={"start"}>
                                <Dropdown.Toggle
                                  variant="outline-primary"
                                  id="dropdown-basic"
                                  size="sm"
                                  className={styles.rowToggle}
                                  disabled={isDisabled}
                                >
                                  <BsThreeDots />
                                </Dropdown.Toggle>

                                <Dropdown.Menu
                                  className={styles.rowMenu}
                                  popperConfig={{ strategy: "fixed" }}
                                  renderOnMount
                                >
                                  {!user.is_deleted && (
                                    <>
                                      <Dropdown.Item
                                        onClick={() => {
                                          setEmail(user.email);
                                          setName(user.name);

                                          if (user.companies) {
                                            setSelectedCompanyOptions(
                                              user.companies.map((company) => ({
                                                label: company.name,
                                                value: company.id,
                                              }))
                                            );
                                          }

                                          if (user.roles) {
                                            setSelectedRoleOptions(
                                              user.roles.map((role) => ({
                                                label: role.name,
                                                value: role.id,
                                              }))
                                            );
                                          }

                                          setEditableIndex(index);
                                        }}
                                        disabled={isDisabled}
                                      >
                                        <BsPencil /> Edit
                                      </Dropdown.Item>

                                      <OverlayTrigger
                                        placement="top"
                                        overlay={(props) => (
                                          <Tooltip
                                            id={`delete-${user.id}-tooltip`}
                                            {...props}
                                          >
                                            User name will be depersonalized.
                                          </Tooltip>
                                        )}
                                      >
                                        <Dropdown.Item
                                          onClick={() => remove(user)}
                                          disabled={
                                            isDisabled || user.is_girteka_user
                                          }
                                        >
                                          <BsTrash /> Delete
                                        </Dropdown.Item>
                                      </OverlayTrigger>

                                      {!user.is_verified && (
                                        <Dropdown.Item
                                          onClick={() => retryInvite(user.id)}
                                          disabled={isDisabled}
                                        >
                                          <BsArrowRepeat /> Invite again
                                        </Dropdown.Item>
                                      )}

                                      <Dropdown.Item
                                        onClick={() => {
                                          setCustomers([user]);
                                          navigate("/bookings");
                                        }}
                                        disabled={
                                          isDisabled || user.is_girteka_user
                                        }
                                      >
                                        <BsFilter /> Filter bookings
                                      </Dropdown.Item>
                                    </>
                                  )}

                                  {user.is_deleted && (
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={(props) => (
                                        <Tooltip
                                          id={`restore-${user.id}-tooltip`}
                                          {...props}
                                        >
                                          Depersonalized user name will not be
                                          restored.
                                        </Tooltip>
                                      )}
                                    >
                                      <Dropdown.Item
                                        onClick={() => restore(user)}
                                        disabled={
                                          isDisabled || user.is_girteka_user
                                        }
                                      >
                                        <BsArrowRepeat /> Restore
                                      </Dropdown.Item>
                                    </OverlayTrigger>
                                  )}

                                  {!!user.owned_bookings_count && (
                                    <Dropdown.Item
                                      onClick={() =>
                                        setSelectedForUnassign(user)
                                      }
                                      disabled={
                                        isDisabled || user.is_girteka_user
                                      }
                                    >
                                      <BsPersonDash /> Unassign bookings
                                    </Dropdown.Item>
                                  )}
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {meta && (
                <div className={styles.loadMore}>
                  {meta.current_page < meta.last_page ? (
                    <Button onClick={() => loadMore()} disabled={editMode}>
                      Load more
                      {isLoading && (
                        <Spinner
                          size="sm"
                          animation="border"
                          className={styles.spinner}
                        />
                      )}
                    </Button>
                  ) : data.length > 10 ? (
                    <span className="text-muted">Nothing more to show.</span>
                  ) : null}
                </div>
              )}

              {selectedForUnassign && (
                <UnassignModal
                  user={selectedForUnassign}
                  onHide={() => setSelectedForUnassign(undefined)}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col />
      </Row>
    </Container>
  );
};

export default UserPage;
